import { useState } from 'react';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

export const SEOSettings = ({ siteData, onUpdate }) => {
  const seo = siteData.seo || {};
  const [title, setTitle] = useState(seo.title || '');
  const [description, setDescription] = useState(seo.description || '');
  const [saving, setSaving] = useState(false);

  const defaultTitle = `${siteData.performer_name}${siteData.tagline ? ` — ${siteData.tagline}` : ''}`;
  const previewTitle = title || defaultTitle;
  const previewDesc = description || siteData.tagline || '';
  const previewUrl = siteData.custom_domain ? `https://${siteData.custom_domain}` : `${window.location.origin}/sites/${siteData.slug}`;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { seo: { title: title.trim(), description: description.trim() } };
      await api.put(`/sites/${siteData.id}`, payload);
      onUpdate(payload);
      toast.success('SEO settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save SEO settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-8" data-testid="seo-settings">
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
            <Search className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">SEO & Browser Title</h3>
            <p className="text-sm text-slate-400">How this site appears in browser tabs, Google and social shares</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm text-slate-300 font-medium">Browser / Search Title</label>
              <span className={`text-xs ${title.length > 60 ? 'text-amber-400' : 'text-slate-500'}`}>{title.length}/60</span>
            </div>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={defaultTitle}
              maxLength={70}
              data-testid="seo-title-input"
              className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm text-slate-300 font-medium">Search Description</label>
              <span className={`text-xs ${description.length > 160 ? 'text-amber-400' : 'text-slate-500'}`}>{description.length}/160</span>
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short sentence describing this site for search results and link previews..."
              rows={2}
              maxLength={200}
              data-testid="seo-description-input"
              className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 resize-none"
            />
          </div>

          {/* Google-style preview */}
          <div className="bg-white rounded-xl p-4" data-testid="seo-google-preview">
            <p className="text-xs text-[#202124] truncate">{previewUrl}</p>
            <p className="text-[#1a0dab] text-lg leading-snug truncate">{previewTitle}</p>
            <p className="text-sm text-[#4d5156] line-clamp-2">{previewDesc || 'Add a description to control how this site appears in search results.'}</p>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} data-testid="seo-save-btn" className="bg-emerald-500 hover:bg-emerald-600 text-white">
              {saving ? 'Saving...' : 'Save SEO'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
